import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Filter } from 'lucide-react';
import { toast } from 'sonner';

// Componentes refactorizados
import { DeleteConfirmDialog } from '../components/shared';
import { SampleCard, FilterPanel } from '../components/dashboard';
import { SEARCH_FIELDS_BY_DESTINATION } from '../constants';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const restoredFilters = location.state?.restoreFilters;
  const [samples, setSamples] = useState([]);
  const [filteredSamples, setFilteredSamples] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [ensayos, setEnsayos] = useState([]);
  const [estados, setEstados] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if user is restricted to a single destination
  const isRestrictedUser = user && !user.is_superuser && user.assigned_destinations?.length === 1;
  const restrictedDestination = isRestrictedUser ? user.assigned_destinations[0] : null;

  const [filters, setFilters] = useState(restoredFilters?.filters || {
    year: 'all',
    month: 'all',
  });

  // Filtro de destino (fijo) - inicializar con destino restringido si aplica
  const [destinationFilter, setDestinationFilter] = useState(restoredFilters?.destinationFilter || 'all');

  const [searchField, setSearchField] = useState(restoredFilters?.searchField || '');
  const [searchValue, setSearchValue] = useState(restoredFilters?.searchValue || '');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(restoredFilters?.currentPage || 1);
  const itemsPerPage = 10;

  // Bulk selection state
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [bulkStatusIds, setBulkStatusIds] = useState(null); // null = not modified
  const [showBulkStatusDropdown, setShowBulkStatusDropdown] = useState(false);
  const [isUpdatingBulk, setIsUpdatingBulk] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sampleToDelete, setSampleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Set default destination filter for restricted users
  useEffect(() => {
    if (isRestrictedUser && restrictedDestination) {
      setDestinationFilter(restrictedDestination);
    }
  }, [isRestrictedUser, restrictedDestination]);

  // Apply filters when samples or filter criteria change
  useEffect(() => {
    applyFilters();
  }, [samples]);

  // Reset to first page only when filter criteria change (not when samples update)
  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [filters, searchValue, searchField, destinationFilter]);

  const fetchData = async () => {
    try {
      const [samplesRes, destRes, ensayosRes, estadosRes] = await Promise.all([
        axios.get('/work-samples'),
        axios.get('/destinations'),
        axios.get('/ensayos'),
        axios.get('/estados'),
      ]);

      setSamples(samplesRes.data);
      setFilteredSamples(samplesRes.data);
      setDestinations(destRes.data);
      setEnsayos(ensayosRes.data);
      setEstados(estadosRes.data);
    } catch (error) {
      toast.error('Error al cargar los datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener campos de búsqueda disponibles según el destino seleccionado
  const getAvailableSearchFields = () => {
    if (destinationFilter === 'all') return [];
    return SEARCH_FIELDS_BY_DESTINATION[destinationFilter] || [];
  };

  const searchFieldLabel = () => {
    const fields = getAvailableSearchFields();
    const field = fields.find(f => f.value === searchField);
    return field ? field.label : 'Buscar';
  };

  // Handler for manual destination changes only (not restoration from state)
  const handleDestinationChange = (value) => {
    setDestinationFilter(value);
    setSearchField('');
    setSearchValue('');
  };

  // Helper function to extract year and month from fecha_entrada (handles multiple formats)
  const extractDateParts = (dateStr) => {
    if (!dateStr) return { year: null, month: null };
    
    // Format: DD/MM/YYYY
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        return { year: parts[2], month: parts[1] };
      }
    }
    
    // Format: YYYY-MM-DD or YYYY-MM-DD HH:MM:SS (ISO format)
    if (dateStr.includes('-')) {
      const datePart = dateStr.split(' ')[0]; // Remove time if present
      const parts = datePart.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        return { year: parts[0], month: parts[1] };
      }
    }
    
    return { year: null, month: null };
  };

  const applyFilters = () => {
    let filtered = [...samples];

    // Filter by destination (fixed filter)
    if (destinationFilter !== 'all') {
      filtered = filtered.filter((s) => s.destination === destinationFilter);
    }

    // Filter by year (using fecha_entrada - F. Entrada GOYA)
    if (filters.year !== 'all') {
      filtered = filtered.filter((s) => {
        const { year } = extractDateParts(s.fecha_entrada);
        return year === filters.year;
      });
    }

    // Filter by month (using fecha_entrada - F. Entrada GOYA)
    if (filters.month !== 'all') {
      filtered = filtered.filter((s) => {
        const { month } = extractDateParts(s.fecha_entrada);
        return month === filters.month;
      });
    }

    // Advanced search filter
    if (searchField) {
      if (searchValue.trim()) {
        filtered = filtered.filter((s) => {
          const fieldValue = s[searchField];
          if (!fieldValue) return false;
          
          // For status_id field, check if the array includes the search value
          if (searchField === 'status_id') {
            if (Array.isArray(fieldValue)) {
              return fieldValue.includes(searchValue);
            }
            return fieldValue === searchValue;
          }
          
          // For other fields, use includes for partial matching
          return fieldValue.toString().toLowerCase().includes(searchValue.toLowerCase());
        });
      } else {
        // No search value: show only samples where the field is empty
        filtered = filtered.filter((s) => {
          const fieldValue = s[searchField];
          if (searchField === 'status_id') {
            return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);
          }
          return !fieldValue || fieldValue.toString().trim() === '';
        });
      }
    }

    setFilteredSamples(filtered);
  };

  const handleDelete = async (id) => {
    setSampleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!sampleToDelete) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`/work-samples/${sampleToDelete}`);
      toast.success('Registro eliminado correctamente');
      fetchData();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar el registro');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSampleToDelete(null);
    }
  };

  const handleEdit = (sample) => {
    // Navigate to entrada-datos with the sample data and current filters
    navigate('/entrada-datos', { 
      state: { 
        editSample: sample,
        dashboardFilters: {
          destinationFilter,
          filters,
          searchField,
          searchValue,
          currentPage,
        }
      } 
    });
  };

  const getEstadoById = (id) => {
    return estados.find((e) => e.id === id);
  };

  // Function to determine if a color is light (needs dark text)
  const isLightColor = (hexColor) => {
    // Convert hex to RGB
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate brightness (perceived luminance)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // If brightness is high, use dark text
    return brightness > 155;
  };

  const clearFilters = () => {
    setFilters({
      year: 'all',
      month: 'all',
    });
    // Don't reset destination filter for restricted users
    if (!isRestrictedUser) {
      setDestinationFilter('all');
    }
    setSearchValue('');
    setSearchField('');
  };

  // Handle inline field update from SampleCard
  const handleFieldUpdate = async (sampleId, field, value) => {
    try {
      await axios.put(`/work-samples/${sampleId}`, { [field]: value });
      setSamples(prev => prev.map(s => s.id === sampleId ? { ...s, [field]: value } : s));
      toast.success('Campo actualizado');
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error('Error al actualizar');
    }
  };

  // Handle payment update
  const handlePaymentUpdate = async (sampleId, paymentIndex, paid, month, billingStatus, year) => {
    try {
      const sample = samples.find(s => s.id === sampleId);
      if (!sample || !sample.payment_config) return;

      const updatedPayments = [...sample.payment_config.payments];
      updatedPayments[paymentIndex] = {
        ...updatedPayments[paymentIndex],
        paid,
        payment_month: paid ? month : null,
        payment_year: paid ? year : null,
        billing_status: billingStatus !== undefined ? billingStatus : (updatedPayments[paymentIndex].billing_status || 'Pte facturar')
      };

      const updatedPaymentConfig = {
        total_amount: sample.payment_config.total_amount,
        payment_type: sample.payment_config.payment_type,
        payments: updatedPayments
      };

      console.log('[DEBUG] Updating payment for sample:', sampleId);
      console.log('[DEBUG] Updated payment config:', JSON.stringify(updatedPaymentConfig, null, 2));

      // Send only payment_config field for update
      await axios.put(`/work-samples/${sampleId}`, {
        payment_config: updatedPaymentConfig
      });

      // Update local state without triggering full reload (keeps current page)
      const updatedSamples = samples.map(s => 
        s.id === sampleId 
          ? { ...s, payment_config: updatedPaymentConfig }
          : s
      );
      setSamples(updatedSamples);
      
      // Also update filtered samples to reflect the change
      setFilteredSamples(prev => prev.map(s => 
        s.id === sampleId 
          ? { ...s, payment_config: updatedPaymentConfig }
          : s
      ));
      
      toast.success('Hito de facturación actualizado correctamente');
    } catch (error) {
      toast.error('Error al actualizar el hito de facturación');
      console.error('Error updating payment:', error);
    }
  };

  // Bulk selection functions
  const toggleSelectSample = (sampleId) => {
    setSelectedSamples(prev => 
      prev.includes(sampleId) 
        ? prev.filter(id => id !== sampleId)
        : [...prev, sampleId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSamples.length === filteredSamples.length) {
      // Deselect all
      setSelectedSamples([]);
    } else {
      // Select all filtered samples
      setSelectedSamples(filteredSamples.map(s => s.id));
    }
  };

  const clearSelection = () => {
    setSelectedSamples([]);
    setBulkStatusIds(null);
    setShowBulkStatusDropdown(false);
  };

  // Estados filtered by current destination
  const destinationEstados = useMemo(() => {
    if (destinationFilter === 'all') return estados;
    return estados.filter(e => e.destinations?.includes(destinationFilter));
  }, [estados, destinationFilter]);

  // Pre-computed status ids from selected samples (intersection)
  const selectedSamplesStatusIds = useMemo(() => {
    if (selectedSamples.length === 0) return [];
    const selected = samples.filter(s => selectedSamples.includes(s.id));
    if (selected.length === 0) return [];
    const first = new Set(selected[0].status_id || []);
    for (let i = 1; i < selected.length; i++) {
      const current = new Set(selected[i].status_id || []);
      for (const id of first) {
        if (!current.has(id)) first.delete(id);
      }
    }
    return Array.from(first);
  }, [selectedSamples, samples]);

  const effectiveBulkStatusIds = bulkStatusIds !== null ? bulkStatusIds : selectedSamplesStatusIds;

  const toggleBulkStatus = (estadoId) => {
    const current = [...effectiveBulkStatusIds];
    const newIds = current.includes(estadoId)
      ? current.filter(id => id !== estadoId)
      : [...current, estadoId];
    setBulkStatusIds(newIds);
  };

  const handleBulkUpdateStatus = async () => {
    if (selectedSamples.length === 0) {
      toast.error('No hay muestras seleccionadas');
      return;
    }
    if (bulkStatusIds === null) {
      toast.error('No has modificado los estados');
      return;
    }

    setIsUpdatingBulk(true);
    try {
      const promises = selectedSamples.map(id =>
        axios.put(`/work-samples/${id}`, { status_id: bulkStatusIds })
      );
      await Promise.all(promises);

      setSamples(prev => prev.map(s =>
        selectedSamples.includes(s.id) ? { ...s, status_id: bulkStatusIds } : s
      ));

      toast.success(`${selectedSamples.length} muestra(s) actualizada(s)`);
      clearSelection();
    } catch (error) {
      console.error('Error updating bulk status:', error);
      toast.error('Error al actualizar las muestras');
    } finally {
      setIsUpdatingBulk(false);
    }
  };

  // Get unique years from samples (using fecha_entrada) - memoized to avoid recalculating
  const availableYears = useMemo(() => {
    const years = new Set();
    samples.forEach((s) => {
      const { year } = extractDateParts(s.fecha_entrada);
      if (year) {
        years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a)); // Most recent first
  }, [samples]);

  const months = [
    { value: '01', label: 'Enero' },
    { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' },
    { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-gray-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-container">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Visualiza y filtra todos los trabajos
          </p>
        </div>
      </div>

      {/* Filters */}
      <FilterPanel
        destinationFilter={destinationFilter}
        onDestinationFilterChange={handleDestinationChange}
        filters={filters}
        onFiltersChange={setFilters}
        searchField={searchField}
        onSearchFieldChange={setSearchField}
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
        destinations={destinations}
        estados={estados}
        availableYears={availableYears}
        availableSearchFields={getAvailableSearchFields()}
        isRestrictedUser={isRestrictedUser}
        restrictedDestination={restrictedDestination}
        filteredCount={filteredSamples.length}
        totalCount={samples.length}
        onClearFilters={clearFilters}
      />

      {/* Results Grid */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Resultados</CardTitle>
            {filteredSamples.length > 0 && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedSamples.length === filteredSamples.length && filteredSamples.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 cursor-pointer"
                  id="select-all"
                />
                <label htmlFor="select-all" className="text-sm text-gray-600 cursor-pointer">
                  Seleccionar todas ({filteredSamples.length})
                </label>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px]">
            {filteredSamples.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No se encontraron registros con los filtros aplicados
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {(() => {
                    // Calculate pagination
                    const totalPages = Math.ceil(filteredSamples.length / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedSamples = filteredSamples.slice(startIndex, endIndex);
                    
                    return paginatedSamples.map((sample) => (
                      <SampleCard
                        key={sample.id}
                        sample={sample}
                        estados={estados}
                        isSelected={selectedSamples.includes(sample.id)}
                        onToggleSelect={toggleSelectSample}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onPaymentUpdate={handlePaymentUpdate}
                        onFieldUpdate={handleFieldUpdate}
                        isSuperuser={user?.is_superuser}
                      />
                    ));
                  })()}
                </div>

                {/* Pagination Controls */}
                {filteredSamples.length > itemsPerPage && (
                  <div className="mt-6 flex items-center justify-between border-t pt-4">
                    <div className="text-sm text-gray-600">
                      Página {currentPage} de {Math.ceil(filteredSamples.length / itemsPerPage)} 
                      {' '}({filteredSamples.length} resultados totales)
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredSamples.length / itemsPerPage), prev + 1))}
                        disabled={currentPage === Math.ceil(filteredSamples.length / itemsPerPage)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Bulk Update Action Bar */}
      {selectedSamples.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-2xl z-50 border-t-4 border-blue-400">
          <div className="container mx-auto px-4 py-4 lg:ml-64">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-white text-blue-600 rounded-full w-8 h-8 flex items-center justify-center font-bold">
                  {selectedSamples.length}
                </div>
                <span className="font-medium">
                  {selectedSamples.length === 1 
                    ? '1 muestra seleccionada' 
                    : `${selectedSamples.length} muestras seleccionadas`}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium">Estados:</label>
                <div className="relative">
                  <Button
                    variant="outline"
                    className="bg-white text-gray-900 hover:bg-gray-100 min-w-[200px] justify-between"
                    onClick={() => setShowBulkStatusDropdown(!showBulkStatusDropdown)}
                    data-testid="dashboard-bulk-status-trigger"
                  >
                    <span className="flex items-center gap-2">
                      {effectiveBulkStatusIds.length > 0
                        ? <>{effectiveBulkStatusIds.length} estado(s) <Badge className="bg-blue-500 text-white text-xs px-1.5 py-0">{effectiveBulkStatusIds.length}</Badge></>
                        : 'Selecciona estados...'}
                    </span>
                  </Button>
                  {showBulkStatusDropdown && (
                    <div className="absolute bottom-full mb-1 left-0 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto text-gray-900" data-testid="dashboard-bulk-status-dropdown">
                      <div className="p-2 border-b">
                        <span className="text-xs font-medium text-gray-500">Selecciona estados</span>
                      </div>
                      {destinationEstados.map((estado) => {
                        const isChecked = effectiveBulkStatusIds.includes(estado.id);
                        return (
                          <label
                            key={estado.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                            data-testid={`dashboard-bulk-status-${estado.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleBulkStatus(estado.id)}
                              className="rounded"
                            />
                            <span
                              className="inline-block w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: estado.color }}
                            />
                            <span className="text-sm">{estado.name}</span>
                          </label>
                        );
                      })}
                      {effectiveBulkStatusIds.length > 0 && (
                        <div className="p-2 border-t">
                          <button
                            className="text-xs text-red-500 hover:text-red-700"
                            onClick={() => setBulkStatusIds([])}
                          >
                            Limpiar estados
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <Button
                  onClick={handleBulkUpdateStatus}
                  disabled={bulkStatusIds === null || isUpdatingBulk}
                  className="bg-green-500 hover:bg-green-600 text-white font-medium"
                >
                  {isUpdatingBulk ? 'Aplicando...' : 'Aplicar'}
                </Button>
                
                <Button
                  onClick={clearSelection}
                  variant="outline"
                  className="bg-white text-gray-700 hover:bg-gray-100"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        title="¿Eliminar registro?"
        description="Esta acción no se puede deshacer. El registro será eliminado permanentemente."
      />
    </div>
  );
};

export default Dashboard;
